import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Token ${token}`;
  }
  return config;
});

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('token');
      localStorage.removeItem('authToken');
      localStorage.removeItem('userRole');
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const login = async (username, password) => {
  try {
    const response = await api.post('/api/api-token-auth/', {
      username,
      password
    });
    
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('userRole', response.data.role);
      return response.data;
    }
    return response.data;
  } catch (error) {
    console.error('Login failed:', error);
    throw error;
  }
};

export const getExchanges = async () => {
  try {
    const response = await api.get('/api/exchanges/');
    return response.data.results || response.data;
  } catch (error) {
    console.error('Error fetching exchanges:', error);
    throw error;
  }
};

export const getExchangeRates = async (params = {}) => {
  try {
    const response = await api.get('/api/exchange-rates/', { params });
    return response.data.results || response.data;
  } catch (error) {
    console.error('Error fetching exchange rates:', error);
    throw error;
  }
};

export const getCurrencies = async (page = 1, pageSize = 10, search = '') => {
  try {
    const params = {
      page: String(page),
      page_size: String(pageSize),
    };
    if (search && String(search).trim()) {
      params.search = String(search).trim();
    }
    const response = await api.get('/api/currencies/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching currencies:', error);
    throw error;
  }
};

export const getCurrency = async (id) => {
  try {
    const response = await api.get(`/api/currencies/${id}/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching currency:', error);
    throw error;
  }
};

export const getCurrencyExchangeRates = async (currencyCode, page = 1, pageSize = 20) => {
  try {
    const capped = Math.min(Math.max(Number(pageSize) || 20, 1), 100);
    const response = await api.get('/api/exchange-rates/', {
      params: {
        currency: currencyCode,
        page: String(page),
        page_size: String(capped),
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching currency exchange rates:', error);
    throw error;
  }
};

export const getInstrument = async (id) => {
  try {
    const response = await api.get(`/api/instruments/${id}/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching instrument:', error);
    throw error;
  }
};

export const getInstrumentPrices = async (instrumentId, page = 1, pageSize = 10) => {
  try {
    const response = await api.get('/api/instrument-prices/', {
      params: {
        instrument: instrumentId,
        page: String(page),
        page_size: String(pageSize),
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching instrument prices:', error);
    throw error;
  }
};

export const getAccounts = async () => {
  try {
    const response = await api.get('/api/accounts/');
    // Django REST Framework returns paginated data in a 'results' field
    return response.data.results || response.data;
  } catch (error) {
    console.error('Error fetching accounts:', error);
    throw error;
  }
};

export const getPortfolios = async (accountId) => {
  try {
    const response = await api.get(`/api/portfolios/?account=${accountId}`);
    // Handle paginated response
    return response.data.results || response.data;
  } catch (error) {
    console.error('Error fetching portfolios:', error);
    throw error;
  }
};

export const getPortfolioList = async (filters = {}) => {
  try {
    const params = {};
    if (filters.account != null && filters.account !== '' && filters.account !== 'all') {
      params.account = String(filters.account);
    }
    if (filters.search && String(filters.search).trim()) {
      params.search = String(filters.search).trim();
    }
    if (filters.reporting_currency && filters.reporting_currency !== 'all') {
      params.reporting_currency = String(filters.reporting_currency).trim();
    }
    const response = await api.get('/api/portfolios/', { params });
    return response.data.results || response.data;
  } catch (error) {
    console.error('Error fetching portfolio list:', error);
    throw error;
  }
};

export const getPortfolio = async (id) => {
  try {
    const response = await api.get(`/api/portfolios/${id}/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching portfolio:', error);
    throw error;
  }
};

export const createPortfolio = async (payload) => {
  try {
    const response = await api.post('/api/portfolios/', payload);
    return response.data;
  } catch (error) {
    console.error('Error creating portfolio:', error);
    throw error;
  }
};

export const updatePortfolio = async (id, payload) => {
  try {
    const response = await api.patch(`/api/portfolios/${id}/`, payload);
    return response.data;
  } catch (error) {
    console.error('Error updating portfolio:', error);
    throw error;
  }
};

export const getPortfolioLiquidity = async (id) => {
  try {
    const response = await api.get(`/api/portfolios/${id}/liquidity/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching portfolio liquidity:', error);
    throw error;
  }
};

export const getPortfolioSummary = async (id) => {
  try {
    const response = await api.get(`/api/portfolios/${id}/summary/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching portfolio summary:', error);
    throw error;
  }
};

export const getPortfolioHoldings = async (id) => {
  const response = await api.get(`/api/portfolios/${id}/holdings/`);
  return response.data;
};

export const getPortfolioNav = async (id) => {
  const response = await api.get(`/api/portfolios/${id}/nav/`);
  return response.data;
};

export const unitizePortfolio = async (id, body = {}) => {
  const response = await api.post(`/api/portfolios/${id}/unitize/`, body);
  return response.data;
};

export const subscribePortfolio = async (id, body) => {
  const response = await api.post(`/api/portfolios/${id}/subscriptions/`, body);
  return response.data;
};

export const redeemPortfolio = async (id, body) => {
  const response = await api.post(`/api/portfolios/${id}/redemptions/`, body);
  return response.data;
};

export const getPortfolioCapitalFlows = async (id, params = {}) => {
  const response = await api.get(`/api/portfolios/${id}/capital-flows/`, { params });
  return response.data;
};

export const settlePortfolioCapitalFlow = async (portfolioId, flowId) => {
  const response = await api.post(
    `/api/portfolios/${portfolioId}/capital-flows/${flowId}/settle/`
  );
  return response.data;
};

export const cancelPortfolioCapitalFlow = async (portfolioId, flowId) => {
  const response = await api.post(
    `/api/portfolios/${portfolioId}/capital-flows/${flowId}/cancel/`
  );
  return response.data;
};

export const getPortfolioPerformance = async (id, params = {}) => {
  try {
    const response = await api.get(`/api/portfolios/${id}/performance/`, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching portfolio performance:', error);
    throw error;
  }
};

export const compareReconFile = async ({ file, reconDate, custodian }) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('recon_date', reconDate);
    if (custodian) {
      formData.append('custodian', custodian);
    }
    const response = await api.post('/api/portfolios/recon/compare/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error comparing recon file:', error);
    throw error;
  }
};

export const preflightPortfolioTrade = async (portfolioId, payload) => {
  try {
    const response = await api.post(`/api/portfolios/${portfolioId}/trade-preflight/`, payload);
    return response.data;
  } catch (error) {
    console.error('Error running trade preflight:', error);
    throw error;
  }
};

export const getPortfolioTargetAllocations = async (id) => {
  try {
    const response = await api.get(`/api/portfolios/${id}/target-allocations/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching portfolio target allocations:', error);
    throw error;
  }
};

export const createPortfolioTargetAllocation = async (id, payload) => {
  try {
    const response = await api.post(`/api/portfolios/${id}/target-allocations/`, payload);
    return response.data;
  } catch (error) {
    console.error('Error creating portfolio target allocation:', error);
    throw error;
  }
};

export const updatePortfolioTargetAllocation = async (id, allocationId, payload) => {
  try {
    const response = await api.patch(
      `/api/portfolios/${id}/target-allocations/${allocationId}/`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error('Error updating portfolio target allocation:', error);
    throw error;
  }
};

export const deletePortfolioTargetAllocation = async (id, allocationId) => {
  try {
    await api.delete(`/api/portfolios/${id}/target-allocations/${allocationId}/`);
  } catch (error) {
    console.error('Error deleting portfolio target allocation:', error);
    throw error;
  }
};


export const getPortfolioRebalanceAnalysis = async (id) => {
  const response = await api.get(`/api/portfolios/${id}/rebalance-analysis/`);
  return response.data;
};

export const proposePortfolioRebalance = async (id, payload = {}) => {
  const response = await api.post(`/api/portfolios/${id}/propose-rebalance/`, payload);
  return response.data;
};

export const getPortfolioProposedTrades = async (id, params = {}) => {
  const response = await api.get(`/api/portfolios/${id}/proposed-trades/`, { params });
  return response.data;
};

export const approveProposedTrade = async (portfolioId, proposalId) => {
  const response = await api.post(
    `/api/portfolios/${portfolioId}/proposed-trades/${proposalId}/approve/`
  );
  return response.data;
};

export const rejectProposedTrade = async (portfolioId, proposalId) => {
  const response = await api.post(
    `/api/portfolios/${portfolioId}/proposed-trades/${proposalId}/reject/`
  );
  return response.data;
};

export const applyModelToPortfolio = async (portfolioId, modelPortfolioId, replace = true) => {
  const response = await api.post(`/api/portfolios/${portfolioId}/apply-model/`, {
    model_portfolio_id: modelPortfolioId,
    replace,
  });
  return response.data;
};

export const getModelPortfolios = async () => {
  const response = await api.get('/api/model-portfolios/');
  return response.data.results || response.data;
};



export const getPerformanceReportStatus = async (portfolioId) => {
  const response = await api.get(`/api/portfolios/${portfolioId}/performance-report/status/`);
  return response.data;
};

export const getSavedPerformanceReport = async (portfolioId, params = {}) => {
  const response = await api.get(`/api/portfolios/${portfolioId}/performance-report/`, { params });
  return response.data;
};

export const generatePerformanceMarketReport = async (portfolioId, body = {}) => {
  const response = await api.post(`/api/portfolios/${portfolioId}/performance-report/`, body);
  return response.data;
};

export const savePerformanceReportMarkdown = async (portfolioId, body) => {
  const response = await api.patch(`/api/portfolios/${portfolioId}/performance-report/`, body);
  return response.data;
};

export const getRebalanceInsightsStatus = async (portfolioId) => {
  const response = await api.get(`/api/portfolios/${portfolioId}/rebalance-insights/status/`);
  return response.data;
};

export const getPortfolioRebalanceInsights = async (portfolioId) => {
  const response = await api.post(`/api/portfolios/${portfolioId}/rebalance-insights/`);
  return response.data;
};

export const getInstruments = async (page = 1, pageSize = 10) => {
  try {
    console.log('Fetching instruments with pagination:', { page, pageSize });
    const params = {
      page: page.toString(),
      page_size: pageSize.toString(),
      pagination_class: 'rest_framework.pagination.PageNumberPagination'
    };
    console.log('Request params:', params);
    const response = await api.get('/api/instruments/', { params });
    console.log('Instruments API response:', response.data);
    console.log('Response count:', response.data.count);
    console.log('Response results length:', response.data.results.length);
    return response.data;
  } catch (error) {
    console.error('Error fetching instruments:', error.response?.data || error.message);
    throw error;
  }
};

export const getAllInstruments = async () => {
  try {
    const pageSize = 200;
    let page = 1;
    let hasMore = true;
    const all = [];

    while (hasMore) {
      const response = await api.get('/api/instruments/', {
        params: {
          page: String(page),
          page_size: String(pageSize),
          pagination_class: 'rest_framework.pagination.PageNumberPagination',
        },
      });

      const data = response.data;
      if (Array.isArray(data)) {
        return data;
      }
      const items = data.results || [];
      all.push(...items);

      if (!data.next || items.length === 0) {
        hasMore = false;
      } else {
        page += 1;
      }
    }

    return all;
  } catch (error) {
    console.error('Error fetching all instruments:', error.response?.data || error.message);
    throw error;
  }
};

export const getTrades = async (page = 1, pageSize = 10, filters = {}) => {
  try {
    const params = {
      page: String(page),
      page_size: String(pageSize),
      timestamp: String(Date.now()),
    };
    if (filters.portfolio != null && filters.portfolio !== '') {
      params.portfolio = String(filters.portfolio);
    }
    if (filters.account != null && filters.account !== '') {
      params.account = String(filters.account);
    }
    if (filters.status && filters.status !== 'all') {
      params.status = filters.status;
    }
    if (filters.operation && filters.operation !== 'all') {
      params.operation = filters.operation;
    }
    if (filters.search && String(filters.search).trim()) {
      params.search = String(filters.search).trim();
    }
    const response = await api.get('/api/trades/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching trades:', error.response?.data || error.message);
    throw error;
  }
};

export const createTrade = async (tradeData) => {
  try {
    console.log('Creating trade with data:', tradeData);
    const response = await api.post('/api/trades/', tradeData);
    console.log('Create trade response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error creating trade:', error.response?.data || error.message);
    if (error.response?.status === 403) {
      throw new Error('You do not have permission to create trades');
    }
    if (error.response?.data) {
      // Pass the actual error response from the API
      throw error;
    }
    throw new Error('Failed to create trade. Please try again.');
  }
};

export default api; 